import { getPreferences } from 'lib/utils'
import { useFormatMessage } from 'lib/localization'
import moment from 'moment'
import PropTypes from 'prop-types'
import { defineMessages } from 'react-intl'

export const messages = defineMessages({
  dateTime: {
    id: 'time.dateTime',
    defaultMessage: '{date} at {time}',
    description: 'For example: "Sept 10, 2018 at 08:58am"'
  },
  todayAt: {
    id: 'time.todayAt',
    defaultMessage: 'Today at {time}'
  },
  yesterdayAt: {
    id: 'time.yesterdayAt',
    defaultMessage: 'Yesterday at {time}'
  }
})

// accepted formats
// https://ridewithgps.com/localizations
const FORMATS = [
  'relative',
  'short',
  'medium',
  'long',
  'dateOnly'
]

const formatMap = {
  short: {
    dmy: 'D/M/YYYY',
    mdy: 'M/D/YYYY',
    ymd: 'YYYY/M/D'
  },
  medium: {
    dmy: 'D MMM, YYYY',
    mdy: 'MMM D, YYYY',
    ymd: 'YYYY MMM D'
  },
  long: {
    dmy: 'D MMMM, YYYY',
    mdy: 'MMMM D, YYYY',
    ymd: 'YYYY MMMM D'
  },
  dateOnly: {
    dmy: 'D MMMM',
    mdy: 'MMMM D',
    ymd: 'MMMM D'
  }
}

const getFormat = props => {
  const fmts = FORMATS.filter(v => props[v])
  if (fmts.length > 0) {
    return fmts[0]
  }

  console.warn(`Unsupported date format`)
}

// `t` is a string with timezone information or a moment object
// `relative` takes precedence over other props
export const DateTime = ({ dateOnly, timeOnly, includeSeconds, relative, ...rest }) => {
  const format = getFormat(rest)
  let { t } = rest
  if (!(t instanceof moment) && moment(t).isValid()) {
    t = moment(t)
  }

  const preference = getPreferences().date_format ?? 'mdy'
  if (relative) {
    return relativeString(t, preference)
  }

  if (timeOnly) {
    return moment.parseZone(t).format(`LT${includeSeconds ? 'S' : ''}`)
  }

  const formatString = formatMap[format][preference]
  if (dateOnly) {
    return moment.parseZone(t).format(formatString)
  }

  const fm = useFormatMessage()
  const dt = moment.parseZone(t)

  return fm(messages.dateTime, { date: dt.format(formatString), time: dt.format(`LT${includeSeconds ? 'S' : ''}`) })
}

const requiredPropsCheck = (props, propName, componentName) => {
  if (!(FORMATS.filter(f => props[f]).length > 0) && !props.relative) {
    return new Error(`One of 'format' or 'relative' is required by '${componentName}' component.`)
  }
}

DateTime.propTypes = {
  t: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(moment)
  ]).isRequired,
  format: requiredPropsCheck,
  relative: requiredPropsCheck,
  dateOnly: PropTypes.bool,
  timeOnly: PropTypes.bool,
  includeSeconds: PropTypes.bool
}

const relativeString = (m, preference) => {
  const fm = useFormatMessage()
  const today = moment().dayOfYear()
  const day = m.dayOfYear()
  const time = m.format('LT')

  if (day === today) {
    return fm(messages.todayAt, { time })
  } else if (day === (today - 1)) {
    return fm(messages.yesterdayAt, { time })
  } else if (m.year() === moment().year()) {
    return fm(messages.dateTime, { date: m.format(formatMap['dateOnly'][preference]), time })
  } else {
    return fm(messages.dateTime, { date: m.format(formatMap['long'][preference]), time })
  }
}
